<template>
  <div class="drop-down__content__wrapper">
    <ul
      ref="dropDownContent"
      data-test="dropdown-content"
      class="drop-down__content tw-bg-core-neutral-warm-100 tw-rounded tw-m-0"
      :class="directionClass"
      :style="{ minWidth: `${dropdownWidth}rem` }">
      <li
        v-if="slots.header && slots.header()"
        class="drop-down-menu-header"
        data-test="drop-down-menu-header">
        <slot name="header" />
      </li>
      <template v-if="isSimpleList">
        <li
          v-for="(item, idx) in groupedOptionList"
          :key="`option-list-${idx}`">
          <DropDownMenuGroupItem
            v-if="item?.showGroupLabel"
            :group="item?.group" />
          <slot
            name="item"
            :item="item" />
        </li>
      </template>
      <DynamicScroller
        v-else
        v-slot="{ item, active, index }"
        ref="scrollerRef"
        :key="dynamicKey"
        :items="groupedOptionList"
        :minItemSize="itemSize"
        class="drop-down-menu__scroller">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :data-index="index"
          :data-test="`drop-down-content-item-${index}`"
          :sizeDependencies="[
            item?.showGroupLabel,
          ]">
          <DropDownMenuGroupItem
            v-if="item?.showGroupLabel"
            :group="item?.group" />
          <slot
            name="item"
            :item="item" />
        </DynamicScrollerItem>
      </DynamicScroller>
      <li
        v-if="slots.footer && slots.footer()"
        class="drop-down-menu-footer"
        data-test="drop-down-menu-footer">
        <slot name="footer" />
      </li>
    </ul>
    <div
      v-if="optionList.length==0"
      class="drop-down__content tw-bg-core-neutral-warm-100 tw-rounded tw-m-0"
      :class="directionClass">
      <p class="body--body-01 s-p-4 tw-text-core-neutral-cold-50">
        {{ t('common.messages.no_results') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import DropDownMenuGroupItem from 'frontcore/views/drop-down/views/unit/DropDownMenuGroupItem.vue'
import i18n from 'frontcore/i18n'
import useDropDownGroupedItems from 'frontcore/views/drop-down/domain/useDropDownGroupedItems'
import { computed, inject, onUnmounted, ref, toRef, useSlots, watch } from 'vue'
import { DROPDOWN_REM_WIDTH } from 'frontcore/views/drop-down/domain/constants'
import {
  DropDownDirection,
  DropDownMenuItemProps,
} from "frontcore/views/drop-down/domain/types"
import { dropDownMenuScrollKey } from 'frontcore/views/drop-down/views/base/types'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'

interface Props {
  optionList: DropDownMenuItemProps[]
  itemSize?: number
  direction?: DropDownDirection
  isSimpleList?: boolean
  width?: number
  keepOrder?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  itemSize: 44,
  direction: DropDownDirection.LEFT,
  isSimpleList: false,
  width: DROPDOWN_REM_WIDTH,
  keepOrder: false,
})

const slots = useSlots()

const directionClass = computed(() => {
  return {
    'drop-down-menu--left': props.direction === DropDownDirection.LEFT,
    'drop-down-menu--right': props.direction === DropDownDirection.RIGHT,
  }
})

const dropdownWidth = props.width ?? DROPDOWN_REM_WIDTH

const { t } = i18n.global

const optionList = toRef(props, 'optionList')
const keepOrder = toRef(props, 'keepOrder')
const { groupedOptionList } = useDropDownGroupedItems(optionList, keepOrder)

const dynamicKeyActive = ref(true)

const dynamicKey = computed(() => `refresh-${dynamicKeyActive.value}`)

const scrollerRef = ref<InstanceType<typeof DynamicScroller>>(null)
const scrollToItemHandler = async (itemId: DropDownMenuItemProps['id']) => {
  await new Promise((r) => setTimeout(r, 0)) // https://github.com/Akryum/vue-virtual-scroller/issues/307#issuecomment-1042320227
  const index = props.optionList.findIndex(({ id }) => id === itemId)
  if (index > 0) {
    scrollerRef.value.scrollToItem(index - 1)
  }
}
const emitter = inject(dropDownMenuScrollKey, null)
emitter?.on('scrollToItemId', scrollToItemHandler)
onUnmounted(() => {
  emitter?.off('scrollToItemId', scrollToItemHandler)
})

watch(() => props.optionList.length, () => {
  dynamicKeyActive.value = !dynamicKeyActive.value
})
</script>

<style scoped>
.drop-down__content {
  border: 1px solid var(--core-neutral-cold-80);
  list-style-type: none;
  min-width: 12rem;
  padding: 0;
  box-sizing: content-box;
}

.drop-down-menu__scroller {
  max-height: 11rem;
}

.drop-down-menu-header:not(:empty) {
  border-bottom: 1px solid var(--core-neutral-cold-80);
  padding: var(--s-1)
}
.drop-down-menu-footer:not(:empty) {
  border-top: 1px solid var(--core-neutral-cold-80);
}

.drop-down-menu--left {
  right: 0;
}

.drop-down-menu--right {
  left: 0;
}
</style>
